import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Button,
  Grid,
  Box,
  Typography,
  Paper,
  TextareaAutosize,
  Divider,
  Hidden,
  Stack,
  Card,
  useTheme,
  CircularProgress
} from '@mui/material';
import { useMediaQuery } from '@mui/material';

import weight from '../assets/Images/weihtage.png';
import shwra from '../assets/Images/shwraimage.svg';
import shwralogo from '../assets/Images/shwralogi.svg';
import ShwraImageMObile from '../assets/Images/Email Header.png';
import { useLocation } from 'react-router';
import jwtDecode from 'jwt-decode';
import axiosInstance, { baseURL } from 'utils/axios';
import { setSession, isValidToken, refreshToken as jwtRefreshViaApi } from 'utils/jwt';
import moment from 'moment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useAuth from 'hooks/useAuth';

/** API / JWT may use "Lawyer" or "lawyer" */
function isLawyerRole(role: string | undefined | null): boolean {
  return typeof role === 'string' && role.toLowerCase() === 'lawyer';
}

/** API may return `Bearer <jwt>` or raw JWT */
function normalizeAccessToken(raw: string | undefined | null): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const t = raw.trim();
  if (t.length === 0) return null;
  if (t.toLowerCase().startsWith('bearer ')) return t.slice(7).trim();
  return t;
}

/** Minimum time between focus/visibility session checks (tabbing between fields). */
const FOCUS_REFRESH_MIN_INTERVAL_MS = 20_000;

/** Refresh before submit if access token expires within this many seconds. */
const SUBMIT_EXPIRY_BUFFER_SEC = 300;

/** Background (interval / focus) refresh if token expires within this many seconds. */
const PROACTIVE_EXPIRY_BUFFER_SEC = 600;

/** Poll session while the user may be typing for a long time (e.g. 20–30+ minutes). */
const SESSION_POLL_INTERVAL_MS = 4 * 60 * 1000;

function secondsUntilExpiry(accessToken: string): number | null {
  try {
    const { exp } = jwtDecode<{ exp: number }>(accessToken);
    return exp - Date.now() / 1000;
  } catch {
    return null;
  }
}

function applyTokensFromCurrentUserResult(result: { token?: string; refreshToken?: string }) {
  const access = normalizeAccessToken(result.token);
  const refresh = typeof result.refreshToken === 'string' ? result.refreshToken.trim() : '';
  if (access && refresh) {
    setSession(access, refresh);
  } else if (access) {
    localStorage.setItem('accessToken', access);
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`;
  }
}

/** GET Account/CurrentUser and apply tokens. Returns whether the call succeeded. */
async function fetchCurrentUserAndApplyStoredTokens(bearer: string): Promise<boolean> {
  try {
    const res = await axiosInstance.get('Account/CurrentUser', {
      headers: { Authorization: `Bearer ${bearer}` }
    });
    if (res?.data?.status === true && res?.data?.result) {
      applyTokensFromCurrentUserResult(res.data.result);
      return true;
    }
  } catch {
    /* session may be expired; caller can refresh */
  }
  return false;
}

function ConsultationForm() {
  const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
  const { user, isInitialized } = useAuth();
  const location = useLocation();
  const theme = useTheme();

  const searchParams = new URLSearchParams(location.search);
  const lawyerId = searchParams.get('lawyerId');
  const appointmentId = searchParams.get('appointmentId');

  const [data, setData] = useState<any>(null);
  const [summary, setSummary] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [error, setError] = useState('');

  const saveReturnParams = useCallback(() => {
    if (lawyerId) localStorage.setItem('saved_lawyerId', lawyerId);
    if (appointmentId) localStorage.setItem('saved_appointmentId', appointmentId);
  }, [lawyerId, appointmentId]);

  const forceLogoutToLawyerLogin = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('_rt');
    window.location.href = '/auth/LawyerLogin';
  }, []);

  const lastFocusRefreshAtRef = useRef(0);
  const refreshChainRef = useRef<Promise<string | null> | null>(null);

  /**
   * 1) POST /Account/RefreshToken via shared JWT helper when `_rt` exists.
   * 2) GET /Account/CurrentUser to apply rotated tokens from `result`.
   */
  const runFullRefreshChain = useCallback(async (): Promise<string | null> => {
    if (refreshChainRef.current) {
      return refreshChainRef.current;
    }

    const p = (async (): Promise<string | null> => {
      let access = localStorage.getItem('accessToken');
      if (!access) return null;

      const rt = localStorage.getItem('_rt');
      if (rt) {
        try {
          await jwtRefreshViaApi();
        } catch {
          // Fall through to CurrentUser.
        }
        access = localStorage.getItem('accessToken');
        if (access && isValidToken(access)) {
          return access;
        }
      }

      access = localStorage.getItem('accessToken');
      if (!access) return null;

      try {
        await fetchCurrentUserAndApplyStoredTokens(access);
      } catch {
        // Session likely dead; caller may redirect.
      }

      const final = localStorage.getItem('accessToken');
      return final && isValidToken(final) ? final : null;
    })();

    refreshChainRef.current = p.finally(() => {
      refreshChainRef.current = null;
    });

    return p;
  }, []);

  type SessionMode = 'focus' | 'interval' | 'submit';

  /** Returns a valid access token or null (caller should redirect to login). */
  const ensureAccessTokenForUse = useCallback(
    async (mode: SessionMode): Promise<string | null> => {
      let access = localStorage.getItem('accessToken');
      if (!access) return null;

      const left = secondsUntilExpiry(access);
      const valid = isValidToken(access);

      if (mode === 'submit') {
        const okForSubmit =
          valid && left !== null && left > SUBMIT_EXPIRY_BUFFER_SEC;
        if (okForSubmit) return access;
        const refreshed = await runFullRefreshChain();
        return refreshed && isValidToken(refreshed) ? refreshed : null;
      }

      const okProactive =
        valid && left !== null && left > PROACTIVE_EXPIRY_BUFFER_SEC;
      if (okProactive) return access;

      const refreshed = await runFullRefreshChain();
      return refreshed && isValidToken(refreshed) ? refreshed : null;
    },
    [runFullRefreshChain]
  );

  /**
   * Throttled focus/visibility: re-check session when user returns to tab or focuses the form
   * (after a long break or while tabbing between fields).
   */
  const syncSessionFromCurrentUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const now = Date.now();
    const accessExpiredOrInvalid = !isValidToken(token);
    if (
      !accessExpiredOrInvalid &&
      now - lastFocusRefreshAtRef.current < FOCUS_REFRESH_MIN_INTERVAL_MS
    ) {
      return;
    }

    lastFocusRefreshAtRef.current = now;
    await ensureAccessTokenForUse('focus');
  }, [ensureAccessTokenForUse]);

  useEffect(() => {
    const onWinFocus = () => {
      void syncSessionFromCurrentUser();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void syncSessionFromCurrentUser();
      }
    };

    window.addEventListener('focus', onWinFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('focus', onWinFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [syncSessionFromCurrentUser]);

  /** While the form is open, periodically refresh tokens before they expire (long typing sessions). */
  useEffect(() => {
    if (!data || data.isFilled === true || submitSuccess || invalidLink) {
      return;
    }

    const id = window.setInterval(() => {
      void ensureAccessTokenForUse('interval');
    }, SESSION_POLL_INTERVAL_MS);

    return () => clearInterval(id);
  }, [data, submitSuccess, invalidLink, ensureAccessTokenForUse]);

  // Auth + load appointment (single flow: validate before calling API)
  const loadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (!lawyerId?.trim() || !appointmentId?.trim()) {
      setInvalidLink(true);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('accessToken');
    const loginLawyerId = localStorage.getItem('lawyerId');

    if (!token) {
      saveReturnParams();
      forceLogoutToLawyerLogin();
      return;
    }

    if (user && !isLawyerRole(user.role)) {
      saveReturnParams();
      forceLogoutToLawyerLogin();
      return;
    }

    if (loginLawyerId !== lawyerId) {
      saveReturnParams();
      forceLogoutToLawyerLogin();
      return;
    }

    const loadKey = `${appointmentId}:${lawyerId}`;
    loadKeyRef.current = loadKey;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setIsError(false);
      setInvalidLink(false);

      try {
        const response = await axiosInstance.post(
          `${baseURL}Appointments/GetAppointmentDetailsByLawyerId?AppointmentId=${appointmentId}&LawyerId=${lawyerId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (cancelled || loadKeyRef.current !== loadKey) return;

        if (response?.data?.status === false) {
          setIsError(true);
          setData(null);
          return;
        }

        if (response?.data?.result) {
          const r = response.data.result;
          setData(r);
          setKeyPoints(r.keyPoints ?? '');
          setSummary(r.summaryOfConsultation ?? '');
        } else {
          setIsError(true);
          setData(null);
        }
      } catch {
        if (!cancelled && loadKeyRef.current === loadKey) {
          setIsError(true);
          setData(null);
        }
      } finally {
        if (!cancelled && loadKeyRef.current === loadKey) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    isInitialized,
    lawyerId,
    appointmentId,
    user,
    saveReturnParams,
    forceLogoutToLawyerLogin
  ]);

  useEffect(() => {
    const handleBack = () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('_rt');
      localStorage.removeItem('refreshToken');
      window.location.href = '/auth/LawyerLogin';
    };

    window.addEventListener('popstate', handleBack);
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('popstate', handleBack);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    if (value.length > 500) {
      setError('ملخص الاستشارة لا يمكن أن يتجاوز 500 حرف.');
    } else {
      setError('');
      setSummary(value);
    }
  };

  /** After access token is valid: call CurrentUser, apply latest tokens to storage, return access token for the next request. */
  const fetchCurrentUserSyncForSubmit = useCallback(async (bearer: string): Promise<string | null> => {
    const ok = await fetchCurrentUserAndApplyStoredTokens(bearer);
    if (!ok) return null;
    const access = localStorage.getItem('accessToken');
    return access && isValidToken(access) ? access : null;
  }, []);

  /** Every focus on form fields: CurrentUser (no throttle). One request at a time to avoid overlap. */
  const inputFocusCheckInFlightRef = useRef(false);

  const onInputFocusCheckSession = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    if (inputFocusCheckInFlightRef.current) {
      return;
    }

    inputFocusCheckInFlightRef.current = true;
    try {
      const bearer = localStorage.getItem('accessToken');
      if (!bearer) return;

      let success = await fetchCurrentUserAndApplyStoredTokens(bearer);
      const accessAfter = localStorage.getItem('accessToken');
      if (!success || !accessAfter || !isValidToken(accessAfter)) {
        const recovered = await runFullRefreshChain();
        if (recovered) {
          await fetchCurrentUserAndApplyStoredTokens(recovered);
        }
      }
    } finally {
      inputFocusCheckInFlightRef.current = false;
    }
  }, [runFullRefreshChain]);

  const submitData = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!appointmentId || !lawyerId) {
      alert('بيانات الموعد غير مكتملة.');
      return;
    }

    const loginLawyerId = localStorage.getItem('lawyerId');
    if (loginLawyerId !== lawyerId) {
      saveReturnParams();
      forceLogoutToLawyerLogin();
      return;
    }

    setLoading(true);

    const summaryHTML = `${summary}`;
    const keyPointsHTML = `${keyPoints}`;

    const postConsultation = (bearer: string) =>
      axiosInstance.post(
        `${baseURL}Appointments/AppointmentWrittenConsultation`,
        {
          SummaryOfConsultation: summaryHTML,
          KeyPoints: keyPointsHTML,
          AppointmentId: appointmentId,
          LoggedInUserId: lawyerId
        },
        {
          headers: {
            Authorization: `Bearer ${bearer}`
          }
        }
      );

    try {
      let token = await ensureAccessTokenForUse('submit');
      if (!token) {
        alert('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى.');
        saveReturnParams();
        forceLogoutToLawyerLogin();
        return;
      }

      let tokenForSubmit = await fetchCurrentUserSyncForSubmit(token);
      if (!tokenForSubmit) {
        const recovered = await runFullRefreshChain();
        if (recovered) {
          tokenForSubmit = await fetchCurrentUserSyncForSubmit(recovered);
        }
      }
      if (!tokenForSubmit) {
        alert('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى.');
        saveReturnParams();
        forceLogoutToLawyerLogin();
        return;
      }

      let response;
      try {
        response = await postConsultation(tokenForSubmit);
      } catch (err: any) {
        const status = err?.response?.status ?? err?.status;
        if (status === 401) {
          const t2 = await runFullRefreshChain();
          if (!t2 || !isValidToken(t2)) {
            alert('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى.');
            saveReturnParams();
            forceLogoutToLawyerLogin();
            return;
          }
          const t3 = await fetchCurrentUserSyncForSubmit(t2);
          if (!t3) {
            alert('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى.');
            saveReturnParams();
            forceLogoutToLawyerLogin();
            return;
          }
          response = await postConsultation(t3);
        } else {
          throw err;
        }
      }

      if (response?.data?.status === false) {
        alert('An error occurred, please try again');
      } else if (response?.data?.status === true) {
        setSubmitSuccess(true);
      } else {
        alert('An error occurred, please try again');
      }
    } catch {
      alert('An error occurred, please try again');
    } finally {
      setLoading(false);
    }
  };

  const appointmentDateLabel = data?.appointmentDate
    ? moment(data.appointmentDate).locale('en').format('DD-MM-YYYY')
    : '—';

  if (!isInitialized) {
    return (
      <Stack justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Stack>
    );
  }

  if (loading && !submitSuccess) {
    return (
      <Stack justifyContent={'center'} alignItems={'center'} height={'100vh'}>
        <CircularProgress />
      </Stack>
    );
  }

  if (invalidLink) {
    return (
      <Stack justifyContent="center" alignItems="center" minHeight="100vh" px={2}>
        <Typography variant="h5" color="error" textAlign="center" gutterBottom>
          رابط غير صالح
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          يرجى فتح الاستشارة من الرابط المرسل إليك (معرّف المحامي ومعرّف الموعد).
        </Typography>
      </Stack>
    );
  }

  if (isError && !data) {
    return (
      <Stack justifyContent="center" alignItems="center" minHeight="100vh" px={2}>
        <Typography variant="h5" color="error" textAlign="center" gutterBottom>
          تعذر تحميل بيانات الموعد
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          تحقق من الاتصال أو حاول مرة أخرى لاحقاً.
        </Typography>
      </Stack>
    );
  }

  if (data?.isFilled === true || submitSuccess) {
    return (
      <Stack justifyContent={'center'} alignItems={'center'} height={'100vh'}>
        <Card
          sx={{
            width: {
              xs: '80%',
              md: '50%',
              height: '50vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column'
            }
          }}
        >
          <CheckCircleIcon
            sx={{
              marginY: 4,
              width: 100,
              height: 100
            }}
            color="primary"
          />
          <Typography variant="h2" color={theme.palette.primary.main}>
            Form Submitted
          </Typography>
          <Typography variant="h3" color={theme.palette.success.dark}>
            Successfully
          </Typography>
        </Card>
      </Stack>
    );
  }

  return (
    <Grid container dir="ltr" spacing={6}>
      <Grid item lg={4} md={5} xs={12} display="flex" justifyContent="center">
        <Hidden mdDown>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <img
              src={shwra}
              alt="shwra"
              style={{ objectFit: 'contain', filter: 'grayscale(100%)' }}
            />
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              <img src={shwralogo} alt="shwraLogo" />
            </Box>
          </Box>
        </Hidden>
        {isSmallScreen && (
          <Box>
            <img src={ShwraImageMObile} alt="shwra" style={{ objectFit: 'contain' }} />
          </Box>
        )}
      </Grid>

      <Grid item lg={8} md={7} xs={12} dir="rtl" sx={{ my: { md: 10, sm: 4 } }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, margin: 'auto', width: '90%' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <img src={weight} alt="" width={50} height={30} />
              <Typography variant="h6" color="primary">
                استشارة قانونية
              </Typography>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                border: 1,
                borderColor: 'primary.main',
                borderRadius: '22px',
                px: 2,
                py: 0.5
              }}
            >
              <svg
                width="14"
                height="11"
                viewBox="0 0 14 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.25 8.12738L1.12249 4.99988L0.0574951 6.05738L4.25 10.2499L13.25 1.24988L12.1925 0.192383L4.25 8.12738Z"
                  fill="#039855"
                />
              </svg>
              <Typography variant="body2" color="primary">
                مكتملة
              </Typography>
            </Box>
          </Box>

          <Box mb={3} px={2}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="medium">
                الرقم المرجعي
              </Typography>
              <Typography variant="body1">{data?.referenceNumber ?? '—'}</Typography>
            </Box>
          </Box>
          <Divider />

          <Box my={3} px={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body1" fontWeight="medium">
                تاريخ الموعد
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <svg
                  width="21"
                  height="22"
                  viewBox="0 0 21 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.7486 2H17.7207V0H15.6648V2H5.38551V0H3.32965V2H2.30172C1.17099 2 0.24585 2.9 0.24585 4V20C0.24585 21.1 1.17099 22 2.30172 22H18.7486C19.8794 22 20.8045 21.1 20.8045 20V4C20.8045 2.9 19.8794 2 18.7486 2ZM18.7486 20H2.30172V9H18.7486V20ZM18.7486 7H2.30172V4H18.7486V7Z"
                    fill="#DDB669"
                  />
                </svg>
                <Typography variant="body1">{appointmentDateLabel}</Typography>
              </Box>
            </Box>
          </Box>
          <Divider />

          <Box my={3} px={2}>
            <Typography variant="h6" gutterBottom>
              تفاصيل الاستشارة:
            </Typography>
            <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 2 }}>
              <Typography variant="body2">{data?.details || '-'}</Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="center" py={4}>
            <Divider sx={{ flex: 1, backgroundColor: 'primary.main' }} />
            <Typography variant="h6" sx={{ mx: 2 }}>
              اضف تفاصيل الصورة
            </Typography>
            <Divider sx={{ flex: 1, backgroundColor: 'primary.main' }} />
          </Box>

          <Box mb={3} px={2}>
            <Typography variant="h6" gutterBottom>
              ملخص موضوع الاستشارة :
            </Typography>
            <TextareaAutosize
              onChange={handleChange}
              onFocus={() => {
                void onInputFocusCheckSession();
              }}
              value={summary}
              minRows={5}
              placeholder="اكتب ملخص الاستشارة (حد أقصى 500 حرف)."
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '8px',
                borderColor: error ? 'red' : 'gray',
                fontSize: '14px'
              }}
            />
            {error && (
              <Typography
                style={{
                  color: 'red',
                  fontSize: '12px',
                  marginTop: '4px'
                }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <Box mb={3} px={2}>
            <Typography variant="h6" gutterBottom>
              النقاط الاساسية :
            </Typography>
            <TextareaAutosize
              onChange={(e) => setKeyPoints(e.target.value)}
              onFocus={() => {
                void onInputFocusCheckSession();
              }}
              value={keyPoints}
              minRows={5}
              placeholder="اكتب النقاط الاساسية للاستشارة."
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '8px',
                borderColor: 'gray',
                fontSize: '14px'
              }}
            />
          </Box>

          <Typography variant="body2" align="center" sx={{ py: 3 }}>
            قبل الظغط على ارسال رجاءا تاكد من المعلومات المكتوبة
          </Typography>

          <Box display="flex" justifyContent="center">
            <Button
              onClick={submitData}
              onFocus={() => {
                void onInputFocusCheckSession();
              }}
              variant="contained"
              color="primary"
              sx={{
                width: '60%'
              }}
            >
              ارســال
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default ConsultationForm;
